import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  SaveButton,
  CancelButton,
  EditButton,
  DeleteButton,
  UploadButton,
  HistoryGridButton,
  EnableButton,
  DisableButton,
  IconButton,
} from '../../buttons';
import { isNil } from 'lodash';

const defaultStyle = {};

const ActionsCell = ({
  id,
  onEnterEdit: onEnterEditProp,
  onExitEdit,
  hideEditButton,
  onSave,
  saveComponent = 'save',
  onCancel,
  onDelete,
  confirmDelete = true, // Keeping for backward compatibility but no longer used
  onEnable,
  onDisable,
  dataItem,
  editField,
  deleteField,
  inEdit,
  disabledSave,
  disabled,
  itemEnabledField = 'isEnabled',
  isLoading,
  onEnterHistory,
  defaultInEdit,
  itemName,
  additionalActions: AdditionalActions = null,
  additionalActionsPlacement = 'end',
  allowModificationField = null,
  modificationDisabledMessage = 'This item cannot be modified',
  readOnly = false,
  style = defaultStyle,
  ...others
}) => {
  const dispatch = useDispatch();
  const onEnterEdit = () => onEnterEditProp(dataItem);

  const isEnableDisableActive = useMemo(() => onEnable && onDisable, [onEnable, onDisable]);
  const isInEdit = editField ? dataItem[editField] : inEdit;

  const getName = () => {
    if (itemName) {
      return typeof itemName === 'string' ? itemName : itemName(dataItem);
    }
    return dataItem?.name ?? '';
  };

  const handleDelete = onDelete;

  // Used to set inner components id and data-testid
  const getContentId = (contentId) => (`${id ? `${id}-` : ''}${contentId}`);

  const SubmitButton = saveComponent === 'upload' ? UploadButton : SaveButton;
  if (isInEdit && !defaultInEdit) {
    return (
      <td className='k-command-cell' style={style}>
        {onSave && !defaultInEdit && (
          <SubmitButton
            id={getContentId('submit-button')}
            type='button'
            iconOnly
            isLoading={isLoading}
            disabled={disabled || disabledSave}
            onClick={onSave}
          />
        )}
        {onCancel && !defaultInEdit && (
          <CancelButton
            id={getContentId('cancel-button')}
            iconOnly
            disabled={disabled || isLoading}
            onClick={onCancel}
          />
        )}
      </td>
    );
  }

  // allows history
  const disableModification = allowModificationField && !dataItem[allowModificationField];

  const disabledActions = disabled || isLoading || dataItem[deleteField];

  return (
    <td className='k-command-cell' title={disabledActions || disableModification ? modificationDisabledMessage : ''} style={style}>
      {additionalActionsPlacement === 'start' && !isNil(AdditionalActions) && (
        <AdditionalActions
          {...others}
          dataItem={dataItem}
        />
      )}
      {
        !hideEditButton && (
          <>
            {onEnterEdit && !defaultInEdit && (disableModification || readOnly) ? (
              <IconButton
                id={getContentId('view-button')}
                icon='eye'
                onClick={onEnterEdit}
                disabled={disabledActions}
                className='view-button'
              />
            ) : (
              <EditButton
                id={getContentId('edit-button')}
                disabled={disabledActions}
                onClick={onEnterEdit}
              />
            )}
          </>
        )
      }
      {onDelete && (
        <DeleteButton
          id={getContentId('delete-button')}
          iconOnly
          isLoading={dataItem[deleteField]}
          disabled={disabledActions || disableModification}
          onClick={handleDelete}
        />
      )}
      {isEnableDisableActive
          && (dataItem[itemEnabledField] ? (
            <DisableButton
              id={getContentId('disable-button')}
              iconOnly
              isLoading={dataItem[deleteField]}
              disabled={disabledActions || disableModification}
              onClick={onDisable}
            />
          ) : (
            <EnableButton
              id={getContentId('enable-button')}
              iconOnly
              isLoading={dataItem[deleteField]}
              disabled={disabledActions || disableModification}
              onClick={onEnable}
            />
          ))}
      {onEnterHistory && (
        <HistoryGridButton
          id={getContentId('history-button')}
          iconOnly
          disabled={disabledActions}
          onClick={onEnterHistory}
        />
      )}
      {additionalActionsPlacement === 'end' && !isNil(AdditionalActions) && (
        <AdditionalActions
          {...others}
          dataItem={dataItem}
        />
      )}
    </td>
  );
};

export default ActionsCell;
